/**
 * Phase 16 Plan 16-03 — TanStack Query v5 hooks for Work Order Management.
 *
 * Mirrors useLogsheetApproverQueries.ts style:
 *  - Typed query/mutation hooks over the work-orders.ts service layer.
 *  - staleTime: 30_000 on list/audit queries; staleTime: 5_000 on detail query.
 *  - Mutations invalidate ['work-orders'] broadly on success (Phase 14 idiom).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/api/error';
import {
  activateWorkOrderRequest,
  addMachinesRequest,
  addSitesRequest,
  cancelWorkOrderRequest,
  completeWorkOrderRequest,
  createWorkOrderRequest,
  getWorkOrderAuditRequest,
  getWorkOrderRequest,
  listWorkOrdersRequest,
  removeMachinesRequest,
  removeSitesRequest,
  reopenWorkOrderRequest,
  updateWorkOrderRequest,
} from '@/services/work-orders';
import type {
  AddRemoveMachinesRequest,
  AddRemoveSitesRequest,
  CancelWorkOrderRequest,
  CompleteWorkOrderResponse,
  CreateWorkOrderRequest,
  PaginatedResult,
  UpdateWorkOrderRequest,
  WorkOrderAuditLogItem,
  WorkOrderDetail,
  WorkOrderListItem,
  WorkOrderListQuery,
} from '@/types/work-orders';

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

/** Paginated list of work orders. */
export function useWorkOrdersListQuery(query: WorkOrderListQuery = {}) {
  return useQuery<PaginatedResult<WorkOrderListItem>, ApiError>({
    queryKey: ['work-orders', 'list', query],
    queryFn: () => listWorkOrdersRequest(query),
    staleTime: 30_000,
  });
}

/** Full detail for a single work order. */
export function useWorkOrderQuery(id: number) {
  return useQuery<WorkOrderDetail, ApiError>({
    queryKey: ['work-orders', 'detail', id],
    queryFn: () => getWorkOrderRequest(id),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 5_000,
  });
}

/** Paginated audit log for a work order. */
export function useWorkOrderAuditQuery(id: number, page = 1) {
  return useQuery<PaginatedResult<WorkOrderAuditLogItem>, ApiError>({
    queryKey: ['work-orders', 'audit', id, page],
    queryFn: () => getWorkOrderAuditRequest(id, { page, pageSize: 20 }),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

/** Create a new work order (Draft). */
export function useCreateWorkOrderMutation() {
  const qc = useQueryClient();
  return useMutation<WorkOrderDetail, ApiError, CreateWorkOrderRequest>({
    mutationFn: (body) => createWorkOrderRequest(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
    },
  });
}

interface UpdateWorkOrderVars {
  readonly id: number;
  readonly body: UpdateWorkOrderRequest;
}

/** Edit a work order (client/description/dates). */
export function useUpdateWorkOrderMutation() {
  const qc = useQueryClient();
  return useMutation<WorkOrderDetail, ApiError, UpdateWorkOrderVars>({
    mutationFn: ({ id, body }) => updateWorkOrderRequest(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
    },
  });
}

interface MachinesVars {
  readonly id: number;
  readonly body: AddRemoveMachinesRequest;
}

/** Add machines to a work order. */
export function useAddMachinesMutation() {
  const qc = useQueryClient();
  return useMutation<WorkOrderDetail, ApiError, MachinesVars>({
    mutationFn: ({ id, body }) => addMachinesRequest(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
    },
  });
}

/** Remove machines from a work order. */
export function useRemoveMachinesMutation() {
  const qc = useQueryClient();
  return useMutation<WorkOrderDetail, ApiError, MachinesVars>({
    mutationFn: ({ id, body }) => removeMachinesRequest(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
    },
  });
}

/** Activate a work order (Draft → Active). */
export function useActivateWorkOrderMutation() {
  const qc = useQueryClient();
  return useMutation<WorkOrderDetail, ApiError, number>({
    mutationFn: (id) => activateWorkOrderRequest(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
    },
  });
}

/** Complete a work order (Active → Completed). */
export function useCompleteWorkOrderMutation() {
  const qc = useQueryClient();
  return useMutation<CompleteWorkOrderResponse, ApiError, number>({
    mutationFn: (id) => completeWorkOrderRequest(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
    },
  });
}

/** Reopen a work order (Completed → Active). */
export function useReopenWorkOrderMutation() {
  const qc = useQueryClient();
  return useMutation<WorkOrderDetail, ApiError, number>({
    mutationFn: (id) => reopenWorkOrderRequest(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
    },
  });
}

interface CancelVars {
  readonly id: number;
  readonly body: CancelWorkOrderRequest;
}

/** Cancel a work order (Any → Cancelled). */
export function useCancelWorkOrderMutation() {
  const qc = useQueryClient();
  return useMutation<WorkOrderDetail, ApiError, CancelVars>({
    mutationFn: ({ id, body }) => cancelWorkOrderRequest(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
    },
  });
}

interface SitesVars {
  readonly id: number;
  readonly body: AddRemoveSitesRequest;
}

/** Add sites to a work order (D-228 multi-site). */
export function useAddSitesMutation() {
  const qc = useQueryClient();
  return useMutation<WorkOrderDetail, ApiError, SitesVars>({
    mutationFn: ({ id, body }) => addSitesRequest(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
    },
  });
}

/** Remove sites from a work order (D-228 multi-site). */
export function useRemoveSitesMutation() {
  const qc = useQueryClient();
  return useMutation<WorkOrderDetail, ApiError, SitesVars>({
    mutationFn: ({ id, body }) => removeSitesRequest(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
    },
  });
}
